import { type CSSProperties, useEffect, useRef } from "react";

import { isTopModal, useModalStack } from "@/lib/modalStack";
import {
  MAX_TEXT_SCALE,
  MIN_TEXT_SCALE,
  type MotionPreference,
  type ThemePreference,
  type UserSettings,
} from "@/lib/settings";
import { SHORTCUT_LIST } from "@/lib/shortcuts";
import { useExitTransition } from "@/lib/useExitTransition";
import { useFocusTrap } from "@/lib/useFocusTrap";

const MODAL_ID = "settings-panel";

interface Props {
  open: boolean;
  settings: UserSettings;
  onClose: () => void;
  onThemeChange: (theme: ThemePreference) => void;
  onMotionChange: (motion: MotionPreference) => void;
  onTextScaleChange: (scale: number) => void;
}

function SegmentedControl<T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
  ariaLabel: string;
}) {
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className="inline-flex rounded-[var(--radius-pill)] bg-[var(--surface-sunken)] p-0.5 shadow-[var(--elev-ring)]"
    >
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(opt.value)}
            className={`type-caption rounded-[var(--radius-pill)] px-3 py-1 font-medium transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)] focus-visible:outline-none focus-visible:shadow-[var(--elev-focus)] ${
              active
                ? "bg-[var(--surface-raised)] text-[var(--text-primary)] shadow-[var(--elev-whisper)]"
                : "text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

export function SettingsPanel({ open, settings, onClose, onThemeChange, onMotionChange, onTextScaleChange }: Props) {
  const dialogRef = useRef<HTMLElement | null>(null);
  const { rendered, closing } = useExitTransition(open);
  useModalStack(MODAL_ID, open);
  useFocusTrap(dialogRef, open);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" && isTopModal(MODAL_ID)) onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!rendered) return null;

  return (
    <>
      <div
        className={`${closing ? "bk-anim-backdrop-out" : "bk-anim-backdrop"} fixed inset-0 z-40 bg-[var(--scrim)]`}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        ref={dialogRef}
        role="dialog"
        aria-label="Settings"
        aria-modal="true"
        className={`${closing ? "bk-anim-drawer-out" : "bk-anim-drawer"} fixed right-0 top-0 z-50 flex h-full w-full flex-col border-l border-[var(--border-default)] bg-[var(--surface-raised)] shadow-[var(--elev-whisper-xl)] sm:max-w-md`}
      >
        <header className="flex items-center justify-between border-b border-[var(--border-default)] px-5 py-4">
          <div className="space-y-0.5">
            <div className="type-overline text-[var(--text-tertiary)]">Preferences</div>
            <div className="type-h2 text-[var(--text-primary)]">Settings</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close settings"
            className="rounded-[var(--radius-pill)] p-1.5 text-[var(--text-secondary)] transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)] hover:bg-[var(--surface-sunken)] hover:text-[var(--text-primary)] focus-visible:outline-none focus-visible:shadow-[var(--elev-focus)]"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M4 4l8 8M12 4l-8 8" />
            </svg>
          </button>
        </header>

        <div className="flex-1 space-y-8 overflow-y-auto p-5">
          <section>
            <div className="type-overline mb-3 text-[var(--text-tertiary)]">Appearance</div>
            <div className="space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="type-body-sm font-medium text-[var(--text-primary)]">Theme</div>
                  <div className="type-caption text-[var(--text-tertiary)]">Auto follows your system preference.</div>
                </div>
                <SegmentedControl
                  value={settings.theme}
                  options={[
                    { value: "auto", label: "Auto" },
                    { value: "light", label: "Light" },
                    { value: "dark", label: "Dark" },
                  ]}
                  onChange={onThemeChange}
                  ariaLabel="Theme preference"
                />
              </div>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="type-body-sm font-medium text-[var(--text-primary)]">Reduce motion</div>
                  <div className="type-caption text-[var(--text-tertiary)]">Disables animations + transitions.</div>
                </div>
                <SegmentedControl
                  value={settings.motion}
                  options={[
                    { value: "auto", label: "Auto" },
                    { value: "reduce", label: "Reduce" },
                  ]}
                  onChange={onMotionChange}
                  ariaLabel="Motion preference"
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="type-body-sm font-medium text-[var(--text-primary)]">Text density</div>
                    <div className="type-caption text-[var(--text-tertiary)]">
                      Scales every text size; spacing unchanged.
                    </div>
                  </div>
                  <span className="type-caption font-mono tabular-nums text-[var(--text-tertiary)]">
                    {Math.round(settings.textScale * 100)}%
                  </span>
                </div>
                <input
                  type="range"
                  min={MIN_TEXT_SCALE}
                  max={MAX_TEXT_SCALE}
                  step={0.125}
                  value={settings.textScale}
                  onChange={(e) => onTextScaleChange(Number.parseFloat(e.target.value))}
                  aria-label="Text density"
                  aria-valuemin={MIN_TEXT_SCALE}
                  aria-valuemax={MAX_TEXT_SCALE}
                  aria-valuenow={settings.textScale}
                  aria-valuetext={`${Math.round(settings.textScale * 100)} percent`}
                  className="bk-range w-full"
                  style={
                    {
                      "--bk-range-fill": `${((settings.textScale - MIN_TEXT_SCALE) / (MAX_TEXT_SCALE - MIN_TEXT_SCALE)) * 100}%`,
                    } as CSSProperties
                  }
                />
              </div>
            </div>
          </section>

          <section>
            <div className="type-overline mb-3 text-[var(--text-tertiary)]">Keyboard shortcuts</div>
            <ul className="space-y-2 rounded-[var(--radius-md)] bg-[var(--surface-sunken)] p-3 shadow-[var(--elev-ring)]">
              {SHORTCUT_LIST.map((s) => (
                <li key={s.display} className="type-body-sm flex items-center justify-between">
                  <span className="text-[var(--text-primary)]">{s.label}</span>
                  <kbd className="type-caption rounded-[var(--radius-sm)] bg-[var(--surface-raised)] px-1.5 py-0.5 font-mono text-[var(--text-secondary)] shadow-[var(--elev-ring)]">
                    {s.display}
                  </kbd>
                </li>
              ))}
            </ul>
          </section>

          <section>
            <div className="type-overline mb-3 text-[var(--text-tertiary)]">About</div>
            <div className="type-body-sm rounded-[var(--radius-md)] bg-[var(--surface-sunken)] p-3 text-[var(--text-secondary)] shadow-[var(--elev-ring)]">
              <div>
                <span className="font-medium text-[var(--text-primary)]">Enterprise RAG</span> — Bekaert
              </div>
              <div className="type-caption mt-1 text-[var(--text-tertiary)]">
                Cited deep-research over the Bekaert knowledge corpus.
              </div>
            </div>
          </section>
        </div>
      </aside>
    </>
  );
}
